import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewsletterForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!name.trim()) newErrors.name = t("newsletter.validation.nameRequired");
    if (!email.trim()) newErrors.email = t("newsletter.validation.emailRequired");
    else if (!EMAIL_RE.test(email.trim())) newErrors.email = t("newsletter.validation.emailInvalid");

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);
    // Push a lead event to the dataLayer (respected by consent mode).
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "generate_lead",
        form_id: "newsletter_home",
      });
    }
    // Simulate submit then navigate to Thank You page (Spanish only route).
    setTimeout(() => {
      navigate("/thank_you");
    }, 300);
  };

  return (
    <form
      data-testid="newsletter-form"
      onSubmit={submit}
      className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-xl"
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="nl-name"
            className="block text-sm text-slate-300 mb-1.5"
          >
            {t("newsletter.nameLabel")}
          </label>
          <input
            id="nl-name"
            data-testid="newsletter-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("newsletter.namePlaceholder")}
            className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
            autoComplete="name"
          />
          {errors.name && (
            <p
              data-testid="newsletter-name-error"
              className="mt-1.5 text-xs text-red-400"
            >
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="nl-email"
            className="block text-sm text-slate-300 mb-1.5"
          >
            {t("newsletter.emailLabel")}
          </label>
          <input
            id="nl-email"
            data-testid="newsletter-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("newsletter.emailPlaceholder")}
            className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
            autoComplete="email"
          />
          {errors.email && (
            <p
              data-testid="newsletter-email-error"
              className="mt-1.5 text-xs text-red-400"
            >
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <p className="mt-4 text-xs text-slate-500 leading-relaxed">
        {t("newsletter.consent")}
      </p>

      <button
        type="submit"
        data-testid="newsletter-submit"
        disabled={submitting}
        className="mt-5 w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 transition-colors"
      >
        {submitting ? t("newsletter.submitting") : t("newsletter.submit")}
      </button>
    </form>
  );
}
